import React, { useState } from "react";
import { List, Typography, Tooltip } from "antd";
import {
    FilePdfOutlined,
    FileImageOutlined,
    FileTextOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FileZipOutlined,
    FileOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import CustomModal from "components/Common/CustomModal";
import NoDataComponent from "components/Common/NoData";
import CustomButton from "components/Common/ui/CustomButton";
import FilePreviewModal from "components/Common/ui/Files/FilePreviewModal";

const { Text } = Typography;

interface AttachmentPreviewModalProps {
    visible: boolean;
    onClose: () => void;
    attachments?: string[] | null; 
    title?: string;
}

const AttachmentPreviewModal: React.FC<AttachmentPreviewModalProps> = ({
    visible,
    onClose,
    attachments = [],
    title,
}) => {
    const { t } = useTranslation();

    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewName, setPreviewName] = useState<string>("");

    const getFileIcon = (url: string) => {
        const extension = url.split(".").pop()?.toLowerCase() || "";

        if (extension === "pdf") {
            return <FilePdfOutlined className="text-danger" style={{ fontSize: 18 }} />;
        } else if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"].includes(extension)) {
            return <FileImageOutlined className="text-success" style={{ fontSize: 18 }} />;
        } else if (["doc", "docx"].includes(extension)) {
            return <FileWordOutlined className="text-primary" style={{ fontSize: 18 }} />;
        } else if (["xls", "xlsx", "csv"].includes(extension)) {
            return <FileExcelOutlined className="text-success" style={{ fontSize: 18 }} />;
        } else if (["zip", "rar", "7z", "tar", "gz"].includes(extension)) {
            return <FileZipOutlined className="text-warning" style={{ fontSize: 18 }} />;
        } else if (["txt", "md", "json", "xml"].includes(extension)) {
            return <FileTextOutlined className="text-secondary" style={{ fontSize: 18 }} />;
        } else {
            return <FileOutlined className="text-muted" style={{ fontSize: 18 }} />;
        }
    };

    const getFileNameFromUrl = (url: string) => {
        try {
            const decodedUrl = decodeURIComponent(url);
            return decodedUrl.split("/").pop() || t("file");
        } catch {
            return url.split("/").pop() || t("file");
        }
    };

    const openPreview = (url: string) => {
        setPreviewUrl(url);
        setPreviewName(getFileNameFromUrl(url));
        setPreviewVisible(true);
    };

    const openInNewTab = (url: string) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const downloadFile = (url: string) => {
        const link = document.createElement("a");
        link.href = url;
        link.download = getFileNameFromUrl(url);
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const hasAttachments = attachments && attachments.length > 0;

    return (
        <>
            <CustomModal
                title={title || t("attachments")}
                isOpen={visible}
                onClose={onClose}
            >
                {!hasAttachments ? (
                    <div className="py-5 text-center">
                        <NoDataComponent />
                    </div>
                ) : (
                    <List
                        dataSource={attachments}
                        renderItem={(url) => (
                            <List.Item className="py-3 border-bottom">
                                <div className="d-flex align-items-center justify-content-between w-100">
                                    <div className="d-flex align-items-center flex-grow-1 me-3">
                                        <div className="me-3">{getFileIcon(url)}</div>
                                        <div className="d-flex flex-column overflow-hidden">
                                            <Tooltip title={getFileNameFromUrl(url)}>
                                                <Text
                                                    strong
                                                    className="mb-1 cursor-pointer text-truncate"
                                                    style={{ maxWidth: "350px" }}
                                                    onClick={() => openPreview(url)} // 👈 use preview
                                                >
                                                    {getFileNameFromUrl(url)}
                                                </Text>
                                            </Tooltip>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <CustomButton
                                            variant="outline-success"
                                            size="sm"
                                            onClick={() => openPreview(url)} // 👈 preview
                                        >
                                            <i className="ti ti-eye me-2"></i>
                                            {t("show")}
                                        </CustomButton>
                                        <CustomButton
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => downloadFile(url)}
                                        >
                                            <i className="ti ti-download me-2 "></i>
                                            {t("download")}
                                        </CustomButton>
                                    </div>
                                </div>
                            </List.Item>
                        )}
                        className="mb-0"
                        style={{ maxHeight: "400px", overflowY: "auto" }}
                    />
                )}
            </CustomModal>

            <FilePreviewModal
                visible={previewVisible}
                url={previewUrl}
                fileName={previewName}
                onClose={() => {
                    setPreviewVisible(false);
                    setPreviewUrl(null);
                }}
            />
        </>
    );
};

export default AttachmentPreviewModal;
